import { Title, Stack, Group, Text, Popover, Divider } from '@mantine/core';
import { Form, FormInsuranceCase, InsuranceFormData } from '../../types';
import { v4 as uuidv4 } from 'uuid';
import styles from './InsuranceCasesSection.module.css';
import InsuranceCaseForm from './InsuranceCaseForm';
import AddInsuranceCasePanel from './AddInsuranceCasePanel';
import { CircleHelpIcon } from 'lucide-react';
import { useDisclosure } from '@mantine/hooks';

interface Props {
  collectionPath: 'insuranceCases';
  form: Form;
}

const InsuranceCasesSection = ({ collectionPath, form }: Props) => {
  const [opened, { close, open }] = useDisclosure(false);
  const insertInsuranceCase = (insuranceCase: FormInsuranceCase) => {
    form.insertListItem(collectionPath, insuranceCase);
  };

  const handleAddNewInsuranceCase = () => {
    const insuranceFormData = form.getValues() as InsuranceFormData;
    const currentProvider = insuranceFormData.insuranceProvider ?? 'arag';

    const newInsuranceCase: FormInsuranceCase = {
      tempId: uuidv4(),
      firestoreId: null,
      caseId: '',
      memberId: '',
      caseValue: '',
      caseType: '',
      otherInsuranceProviderName: currentProvider === 'other' ? '' : null,
    };
    insertInsuranceCase(newInsuranceCase);
  };

  const insuranceCases =
    (form.getValues() as InsuranceFormData).insuranceCases ?? [];

  return (
    <section>
      <Stack gap='xs'>
        <Group align='center' gap='xs'>
          <Title order={2} size='h3'>
            Insurance Cases
          </Title>
          <Popover withArrow opened={opened} onClose={close} shadow='md'>
            <Popover.Target>
              <CircleHelpIcon
                size={16}
                color='var(--mantine-color-dimmed)'
                onMouseEnter={open}
                onMouseLeave={close}
              />
            </Popover.Target>
            <Popover.Dropdown maw='400px'>
              <Stack gap='sm'>
                <Text fw={600} fz='sm'>
                  What's the difference between an 'insurance case' and a
                  'matter' in Practist?
                </Text>
                <Stack gap='xs'>
                  <Divider />
                  <Text fz='sm'>
                    The short answer is that{' '}
                    <Text fz='sm' fw={600} display='inline'>
                      one 'matter' can have multiple 'insurance cases'
                    </Text>
                    .
                  </Text>
                  <Text fz='sm'>The longer answer:</Text>
                  <Group gap='2'>
                    <Text fz='sm'>
                      An{' '}
                      <Text fz='sm' fw={600} display='inline'>
                        insurance case
                      </Text>{' '}
                      represents a single case in the insurance provider's
                      system. This info is used when filing a claim.
                    </Text>
                  </Group>
                  <Text fz='sm' td='italic'>
                    For example - In ARAG, an insurance case is identified by
                    its CaseAssist number.
                  </Text>
                </Stack>
                <Stack gap='xs'>
                  <Text fz='sm'>
                    A{' '}
                    <Text fz='sm' fw={600} display='inline'>
                      matter
                    </Text>{' '}
                    represents an agreement between the client and the attorney.
                    When the client is using legal insurance, it sometimes makes
                    sense to bundle multiple insurance cases into a single
                    matter.
                  </Text>
                  <Text fz='sm'>
                    For example - An ARAG estate planning matter often includes
                    two CaseAssist numbers: One for the Revocable Trust and one
                    for Will Preparation.
                  </Text>
                </Stack>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        </Group>

        <div className={styles.insuranceCasesContainer}>
          {insuranceCases.length > 0 && (
            <ul>
              {insuranceCases.map((insuranceCase, index) => (
                <li key={insuranceCase.tempId}>
                  <InsuranceCaseForm
                    key={insuranceCase.tempId}
                    insuranceCaseIndex={index}
                    canDelete={insuranceCases.length > 1}
                    insuranceProvider={
                      (form.getValues() as InsuranceFormData).insuranceProvider
                    }
                    insuranceCase={insuranceCase}
                    onChange={(updatedCase: FormInsuranceCase) => {
                      form.removeListItem(collectionPath, index);
                      form.insertListItem(collectionPath, updatedCase, index);
                    }}
                    onDelete={() => {
                      form.removeListItem(collectionPath, index);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          <Stack gap='0'>
            <AddInsuranceCasePanel
              panelTitle='Add Insurance Case'
              accessibilityLabel='Add an insurance case'
              onAddInsuranceCase={handleAddNewInsuranceCase}
            />
          </Stack>
        </div>
      </Stack>
    </section>
  );
};

export default InsuranceCasesSection;
